import { EventPriority } from 'library/ts/events/EventPriority';
import { IEventCallbackVO } from 'library/ts/events/interfaces/IEventCallbackVO';
import { IEventCallbackVOSubscriber } from 'library/ts/events/interfaces/IEventCallbackVOSubscriber';
import { IEventContextVO } from 'library/ts/events/interfaces/IEventContextVO';
import { IEventDispatcher } from 'library/ts/events/interfaces/IEventDispatcher';
import { IEventVO } from 'library/ts/events/interfaces/IEventVO';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';

/**
 * @description EventDispatcher based on {@link http://michd.me/blog/event-driven-javascript-a-simple-event-dispatcher/}<br>
 * Reference to this class should exist in every class interested in application wide events.
 *
 * **Replay buffer:** maintains a rolling 100-entry buffer of every dispatched event.
 * `getReplayBuffer()` exposes a snapshot for NerdMode capture backfill — events that fired
 * before the capture system initialized are prepended to the log as `isReplay: true` entries.
 * The buffer is always active (not gated on NerdMode) and cleared on `clear()`.
 */
export class EventDispatcher implements IEventDispatcher {

	static readonly #REPLAY_BUFFER_CAP = 100;

	// owner class of this EventDispatcher instance
	#ownerName: string;

	/**
	 * @description holder for event subscribers
	 * @type {Record<string, IEventCallbackVO[]>}
	 * @private
	 */
	#eventSubscriptions: Record<string, IEventCallbackVO[]> = {};

	/**
	 * @description Rolling buffer of the last N dispatched events — used by NerdMode capture
	 * to replay events that fired before the capture system was initialized.
	 */
	#replayBuffer: IEventVO[] = [];

	constructor( ownerName: string = undefined ) {
		this.#ownerName = ownerName;
	}

	get name(): string {
		return 'EventDispatcher';
	}

	/**
	 * @description Name of owner class of this EventDispatcher instance
	 * @returns {string}
	 */
	get ownerName(): string {
		return this.#ownerName;
	}

	/**
	 * @description Returns reference to application wide Utilities
	 * @returns {Utilities | undefined}
	 */
	get utils() {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	/**
	 * @description Returns event subscriptions object
	 * @returns {{totalEvents: number, totalListeners: number, subscriptions: Record<string, IEventCallbackVO[]>}}
	 */
	getEventSubscribers = (): { totalEvents: number, totalListeners: number, subscriptions: Record<string, IEventCallbackVO[]> } => {
		let totalEvents: number = 0;
		let totalListeners: number = 0;

		for ( const key in this.#eventSubscriptions ) {
			if ( Object.prototype.hasOwnProperty.call( this.#eventSubscriptions, key ) ) {
				totalEvents++;
				totalListeners += ( this.#eventSubscriptions[ key ] ).length;
			}
		}

		return {
			totalEvents: totalEvents,
			totalListeners: totalListeners,
			subscriptions: this.#eventSubscriptions
		};
	};

	/**
	 *  @description Subscribe to event (add event listener)
	 * @param {string | IEventContextVO} event
	 * @param {(event: IEventVO) => void} callback
	 * @param {IEventCallbackVOSubscriber} subscriber
	 */
	addEventListener = ( event: string | IEventContextVO, callback: ( event: IEventVO ) => void, subscriber: IEventCallbackVOSubscriber = undefined ): void => {
		let eventType: string;
		let timesToDispatch: number;
		let priority: EventPriority = EventPriority.NORMAL;

		// Use case #1 => event is a string
		if ( this.utils.Type.isString( event ) ) {
			eventType = event as string;
		}

		// Use case #2 => event is an object
		if ( this.utils.Type.isObject( event ) && !this.utils.Type.isEmpty( event ) ) {
			eventType = ( event as IEventContextVO )?.type;
			timesToDispatch = ( event as IEventContextVO )?.timesToDispatch;
			priority = ( event as IEventContextVO )?.priority || EventPriority.NORMAL;
		}

		// Sanity check => do not subscribe to invalid event, callback
		if ( this.utils.Type.isEmpty( eventType ) || !this.utils.Type.isFunction( callback ) ) {
			// @SONAR_START_NO_SCAN@
			console.warn(
				this.name,
				'Event subscription ignored:',
				'event:',
				event,
				'callback:',
				callback,
				'subscriber.name:',
				subscriber?.name
			);
			// @SONAR_END_NO_SCAN@
			return;
		}

		// Retrieve a list of current subscribers for eventName (if any)
		let subscribers: IEventCallbackVO[] = this.#eventSubscriptions[ eventType ];

		if ( this.utils.Type.isUndefined( subscribers ) ) {
			// If no subscribers for this event were found,
			// initialize a new empty array
			subscribers = this.#eventSubscriptions[ eventType ] = [];
		}

		// Add the given callback function to the end of the array with
		// eventSubscriptions for this event.
		subscribers.push(
			{
				callback: callback,
				timesToDispatch: timesToDispatch,
				subscriber: subscriber,
				subscriberName: subscriber?.name,
				priority: priority
			} as IEventCallbackVO
		);

		// sort subscribers per value of subscriber.priority
		subscribers.sort( this.utils.Array.sortByProperty( 'priority', false ) );
	};

	/**
	 * @description  Un-subscribe from event (remove event listener)
	 * @param {string} eventName
	 * @param {(event: IEventVO) => void} callback
	 * @return {boolean}
	 */
	removeEventListener = ( eventName: string, callback: ( event: IEventVO ) => void ): boolean => {
		// remove event if there are no more subscribers
		const removeEvent = ( event: string ) => {
			if ( this.#eventSubscriptions[ event ]?.length === 0 ) {
				delete this.#eventSubscriptions[ event ];
			}
		};

		if ( this.#eventSubscriptions[ eventName ] ) {
			const subscribers: IEventCallbackVO[] = this.#eventSubscriptions[ eventName ];
			for ( let i: number = subscribers?.length - 1; i >= 0; --i ) {
				if ( subscribers[ i ]?.callback === callback ) {
					// remove subscriber
					subscribers.splice( i, 1 );
					// remove event if there are no more subscribers
					removeEvent( eventName );
					// sort subscribers per value of subscriber.priority
					subscribers?.sort( this.utils.Array.sortByProperty( 'priority', false ) );
					return true;
				}
			}
		}
		return false;
	};

	/**
	 * @description Un-subscribes from all events on all subscribers
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	clear = (): Promise<IPromiseResolveGeneric> => {
		/**
		 * @description Unsubscribes all listeners from one event
		 * @param {string} eventType
		 */
		const unsubscribeFromEvent = ( eventType: string ): void => {
			const subscribers = this.#eventSubscriptions[ eventType ];
			if ( this.utils.Type.isArray( subscribers ) && !this.utils.Type.isEmpty( subscribers ) ) {
				subscribers.forEach(
					( subscriber ) => {
						this.removeEventListener( eventType, subscriber.callback );
					}
				);
			}
		};

		return new Promise(
			( resolve ) => {
				for ( const eventType in this.#eventSubscriptions ) {
					if ( Object.prototype.hasOwnProperty.call( this.#eventSubscriptions, eventType ) ) {
						unsubscribeFromEvent( eventType );
						delete this.#eventSubscriptions[ eventType ];
					}
				}
				this.#eventSubscriptions = {};
				// Clear replay buffer so stale events from the previous session
				// are not picked up by a subsequent NerdMode capture start.
				this.#replayBuffer = [];
				resolve( { msg: 'done' } );
			}
		);
	};

	/**
	 * @description Returns a chronological copy of the replay buffer — the last
	 * {@link EventDispatcher.#REPLAY_BUFFER_CAP} events dispatched through this instance.
	 * Used by NerdModeEventCapture to backfill the initialization sequence when capture
	 * starts after the player is born.
	 *
	 * Returns `.slice()` — a new array each call; safe to hold without retaining a reference
	 * to the internal buffer. Empty array if no events have been dispatched yet.
	 * @returns Copy of the replay buffer
	 */
	getReplayBuffer = (): IEventVO[] => {
		return this.#replayBuffer.slice();
	};

	/**
	 * @description  Dispatch event
	 * @param {IEventVO} eventVO
	 * @param {object} context
	 */
	dispatchEvent = ( eventVO: IEventVO, context: object = this ): void => {
		// Record in replay buffer before dispatching — captured regardless of subscriber count
		if ( eventVO ) {
			this.#replayBuffer.push( eventVO );
			if ( this.#replayBuffer.length > EventDispatcher.#REPLAY_BUFFER_CAP ) {
				this.#replayBuffer.shift();
			}
		}

		// Retrieve a list of subscribers for the event being triggered
		const subscribers: IEventCallbackVO[] = this.#eventSubscriptions[ eventVO?.type ];

		// No subscribers wre found for this event or event is undefined, return early to abort execution
		if ( this.utils.Type.isUndefined( subscribers ) ) {
			return;
		}

		/**
		 * @description Checks number of times event should be dispatched before unsubscribing
		 * @param {IEventCallbackVO[]} subs
		 * @param {number} myIndex
		 */
		const processNumberOfDispatches = ( subs: IEventCallbackVO[], myIndex: number ): void => {
			// check number of times event should be dispatched before unsubscribing
			if ( this.utils.Type.isNumber( subs[ myIndex ]?.timesToDispatch ) && subs[ myIndex ]?.timesToDispatch > 0 ) {
				// @SONAR_START_NO_SCAN@
				console.log( this.name, 'dispatchEvent:', eventVO?.type, 'timesToDispatch:', subs[ myIndex ]?.timesToDispatch, 'Dispatch + Decrement' );
				// @SONAR_END_NO_SCAN@

				--subs[ myIndex ].timesToDispatch; // decrement dispatched times counter

				if ( subs[ myIndex ]?.timesToDispatch === 0 ) {
					// @SONAR_START_NO_SCAN@
					console.log( this.name, 'dispatchEvent:', eventVO?.type, 'timesToDispatch:', subs[ myIndex ]?.timesToDispatch, 'Unsubscribe' );
					// @SONAR_END_NO_SCAN@
					delete subs[ myIndex ]?.timesToDispatch;
					delete subs[ myIndex ]?.subscriber;
					delete subs[ myIndex ]?.subscriberName;

					/**
					 * Auto-unsubscribe listener from event that has reached 0 timesToDispatch
					 *
					 * **BUG FIX (2025-10-02):**
					 * Previously, this code called BOTH `removeEventListener()` AND `splice()`:
					 *   - `removeEventListener()` iterates through subscribers array and removes matching callback
					 *   - `splice()` directly removes element at current index
					 *
					 * This double-removal caused a bug where OTHER subscribers (not just the one-time subscriber)
					 * were accidentally removed during the iteration/removal process. Specifically, when one
					 * subscriber with `timesToDispatch: 1` was removed, it could inadvertently remove a nearby
					 * permanent subscriber from the same event (e.g., ErrorManager's ON_FATAL_ERROR subscription).
					 *
					 * **THE FIX:**
					 * Use ONLY `splice()` for direct removal since we already have the exact index.
					 * No need for `removeEventListener()` which would cause redundant iteration and potential
					 * index confusion during the removal process.
					 */
					subs.splice( myIndex, 1 );
				}
			}
		};

		// get array length (its our index)
		let index: number = subscribers?.length;

		while ( ( this.utils.Type.isNumber( index ) && index > 0 ) ) {
			index--; // decrement index

			if ( this.utils.Type.isDefined( subscribers[ index ] ) && this.utils.Type.isDefined( subscribers[ index ]?.callback?.apply ) ) {
				// execute callback (dispatch event to subscriber)
				subscribers[ index ]?.callback?.call( context, eventVO );
				// process number of times event should be dispatched use case
				processNumberOfDispatches( subscribers, index );
			} else {
				// @SONAR_START_NO_SCAN@
				// ignore anonymous subscribers
				console.log( this.name, 'dispatchEvent: undefined subscriber -> do not dispatch event', subscribers[ index ] );
				// @SONAR_END_NO_SCAN@
			}
		}

	};

}